import { Component } from 'react';

/**
 * ErrorBoundary — Round 97a (Feb 2026).
 *
 * Catches React render errors anywhere below it in the tree so the
 * app never hard-crashes to a blank/black screen the way it did on
 * production when the Non-Dividend Quality Scorer modal hit an
 * exception. Shows a small recovery card with a Reload button.
 *
 * Why class component? React's error boundary contract still only
 * supports `componentDidCatch` / `getDerivedStateFromError` on class
 * components — there is no hook equivalent.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Surface in the console for debugging — production users won't
    // see this but the dev tools tab will if they peek.
    console.error('[ErrorBoundary] caught render error:', error, info);
  }

  handleReload = () => {
    try {
      window.location.reload();
    } catch {
      // no-op
    }
  };

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const msg = (this.state.error && (this.state.error.message || String(this.state.error))) || 'Unknown error';
    return (
      <div
        className="min-h-screen flex items-center justify-center p-6 bg-background"
        data-testid="app-error-boundary"
      >
        <div className="max-w-md w-full rounded-lg border border-border bg-card p-6 shadow-xl space-y-3">
          <div className="text-sm font-bold text-rose-400">Something broke on this page</div>
          <div className="text-xs text-muted-foreground">
            The app caught a render error and stopped before it could black-screen.
            Try reloading the page — your data is safe.
          </div>
          <div className="text-[10px] font-mono text-muted-foreground bg-muted/30 rounded p-2 break-words max-h-32 overflow-auto">
            {msg}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={this.handleReload}
              data-testid="error-boundary-reload"
              className="flex-1 px-3 py-2 rounded-md text-sm font-medium bg-primary text-primary-foreground hover:opacity-90"
            >
              Reload page
            </button>
            <button
              type="button"
              onClick={this.handleReset}
              data-testid="error-boundary-dismiss"
              className="flex-1 px-3 py-2 rounded-md text-sm font-medium bg-muted/40 hover:bg-muted/60 text-foreground"
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    );
  }
}
